/**
 * Which sheets actually need Univer to RECALCULATE, read from the .xlsx itself.
 *
 * A formula cell in an .xlsx stores its last computed result beside the formula:
 *
 *     <c r="H11"><f>…</f><v>98.9</v></c>     computed, answer 98.9
 *     <c r="P20"><f>…</f><v/></c>            computed, answer "" (an EMPTY string)
 *     <c r="X1"><f>…</f></c>                 NEVER computed — no cached result at all
 *
 * Only the third kind needs recalculating. ExcelJS cannot tell the second from the
 * third — `cell.result` is `undefined` for both — so a check written against ExcelJS
 * counts every deliberately-empty cell as uncached. On the Metals_ICP_OES template
 * that is 820 cells out of 1,227, none of which are genuinely uncached, and the
 * mistake made Univer recompute the entire sheet: 185 SECONDS in a headless
 * measurement, which is the sheet "hanging" while the analyst scrolls (client
 * 2026-08-24). Conditional formatting suffered the same way, since it evaluates
 * whatever the cells hold at the time and they were still empty.
 *
 * Reading the raw XML is the only way to separate the two, so that is what this does:
 * a single regex pass per sheet, no cell objects materialised.
 */
import JSZip from 'jszip';

/** Per sheet NAME, cells keyed `"row,col"` 1-based to match ExcelJS coordinates. */
export type CellSets = Map<string, Set<string>>;
export type UncachedCells = CellSets;

export interface FormulaCacheInfo {
  /** Formula cells with NO `<v>` at all — DevExpress never wrote a result for them. It
   *  omits `<v>` for a ZERO result, so these are also the only possible "dropped zeros". */
  uncached: CellSets;
  /** Formula cells whose cached result is not a real value: no `<v>` OR `<v/>`. These are
   *  the ones whose answer can CHANGE when DevExpress recalculates on load, so they are
   *  the ones Univer must compute. A cell with `<v>x</v>` keeps its value. */
  unresolved: CellSets;
  /** The cached result of every formula cell that HAS one, straight from the XML.
   *
   *  ExcelJS silently loses some of these: a formula returning a STRING in a cell whose
   *  number format is a DATE (`t="str"` + numFmt "mm/dd/yyyy hh:mm") comes back as
   *  `result: null`, because it tries to coerce the text to a Date and gives up. The value
   *  is right there in the file — `<v>08/13/2026 12:37</v>` — and LIMS shows it. That
   *  accounted for 125 blank cells across 8 templates (client 2026-08-24). `t` is the
   *  cell's OOXML type: "str" = literal string, "e" = error, absent = numeric. */
  cachedValues: Map<string, Map<string, { v: string; t?: string }>>;
}

/** XML entity decode for a cached value read straight out of the sheet part. */
const decodeXml = (s: string): string =>
  s.replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'").replace(/&#(\d+);/g, (_m, d: string) => String.fromCharCode(Number(d)))
    .replace(/&amp;/g, '&');            // last, so "&amp;lt;" does not become "<"

const colToNum = (s: string): number => {
  let v = 0;
  for (const ch of s) v = v * 26 + (ch.charCodeAt(0) - 64);
  return v;
};

/**
 * Every formula cell that was NEVER computed, per sheet.
 *
 * Two callers need this and both were getting it wrong from ExcelJS:
 *   • whether a sheet must be recalculated at all (see readSheetsNeedingRecalc);
 *   • whether a blank QC cell is a DevExpress "dropped zero". DevExpress omits the
 *     `<v>` entirely for a zero result, so a dropped zero really is an uncached
 *     cell — but `<v/>` (computed, answer "") looks identical through ExcelJS, and
 *     treating those as zeros painted blank RPD % cells green, since 0 is inside
 *     the control limit (client 2026-08-24).
 *
 * Never throws: an unreadable workbook returns null and callers keep their previous
 * behaviour.
 */
export async function readUncachedFormulaCells(buf: ArrayBuffer): Promise<UncachedCells | null> {
  return (await readWorkbook(buf))?.uncached ?? null;
}

/** Both sets in one pass — see {@link FormulaCacheInfo}. */
export async function readFormulaCacheInfo(buf: ArrayBuffer): Promise<FormulaCacheInfo | null> {
  return readWorkbook(buf);
}

/**
 * Names of the sheets holding at least one formula cell with NO cached result.
 * A sheet absent from this set is fully computed already and must be handed to
 * Univer as VALUES — recalculating it can only be slower and less faithful.
 *
 * Never throws: an unreadable workbook returns null, and the caller then keeps
 * whatever behaviour it had before.
 */
export async function readSheetsNeedingRecalc(buf: ArrayBuffer): Promise<Set<string> | null> {
  const info = await readWorkbook(buf);
  if (!info) return null;
  const out = new Set<string>();
  for (const [sheet, set] of info.uncached) if (set.size) out.add(sheet);
  return out;
}

async function readWorkbook(buf: ArrayBuffer): Promise<FormulaCacheInfo | null> {
  try {
    const zip = await JSZip.loadAsync(buf);

    const wbXml = await zip.file('xl/workbook.xml')?.async('string');
    if (!wbXml) return null;
    // Sheet NAME and r:id in document order, so a result can be keyed by name.
    const sheets = [...wbXml.matchAll(/<sheet\b[^>]*?name="([^"]*)"[^>]*?r:id="([^"]+)"/g)]
      .map(m => ({ name: m[1]!, rid: m[2]! }));
    if (!sheets.length) return null;

    const rels = (await zip.file('xl/_rels/workbook.xml.rels')?.async('string')) ?? '';
    const targetOf = (id: string): string | null => {
      const m = rels.match(new RegExp(`<Relationship\\b[^>]*Id="${id}"[^>]*Target="([^"]+)"`))
        ?? rels.match(new RegExp(`<Relationship\\b[^>]*Target="([^"]+)"[^>]*Id="${id}"`));
      return m?.[1] ?? null;
    };

    const uncached: CellSets = new Map();
    const unresolved: CellSets = new Map();
    const cachedValues: Map<string, Map<string, { v: string; t?: string }>> = new Map();
    for (const { name, rid } of sheets) {
      const target = targetOf(rid);
      if (!target) continue;
      const path = ('xl/' + target).replace(/^xl\/\/+/, 'xl/').replace(/\/\.\//g, '/');
      const xml = await zip.file(path)?.async('string');
      if (!xml) continue;
      const noV = new Set<string>();
      const noValue = new Set<string>();
      const values = new Map<string, { v: string; t?: string }>();
      /* The `/>` alternative is essential: an EMPTY cell is written self-closing
         (`<c r="B7" s="1"/>`), and without it the body match runs on to the NEXT `</c>`
         and swallows every cell in between. That silently hid the later columns — which is
         why the recovered values stopped at column 27 and AE was never seen. */
      for (const m of xml.matchAll(/<c r="([A-Z]+)(\d+)"([^>]*?)(?:\/>|>([\s\S]*?)<\/c>)/g)) {
        const attrs = m[3]!, body = m[4] ?? '';
        if (!/<f[\s>/]/.test(body)) continue;             // not a formula cell
        const ref = `${Number(m[2])},${colToNum(m[1]!)}`;
        const vm = body.match(/<v>([\s\S]*?)<\/v>/);
        const hasRealValue = !!vm && vm[1]!.length > 0;
        if (!/<v[\s>/]/.test(body)) noV.add(ref);          // no <v> element at all
        if (!hasRealValue) noValue.add(ref);               // no <v>, or <v/>, or <v></v>
        else {
          const t = attrs.match(/\bt="([^"]+)"/)?.[1];
          // Shared strings are an INDEX, not text — a formula cell never uses `t="s"`,
          // but skip it rather than store a number that would render as one.
          if (t !== 's') values.set(ref, t ? { v: decodeXml(vm![1]!), t } : { v: decodeXml(vm![1]!) });
        }
      }
      uncached.set(name, noV);
      unresolved.set(name, noValue);
      cachedValues.set(name, values);
    }
    return { uncached, unresolved, cachedValues };
  } catch {
    return null;                                          // caller keeps its previous behaviour
  }
}
